import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

// ====================================================================
// Push-Panel bridge — lets <Sheet side="right"> portal into the docked
// right column (managed by RightPanelProvider) instead of rendering as
// a Radix overlay. Set by RightPanelProvider via setPanelRef().
// ====================================================================

type PanelAPI = {
  isOpen: boolean;
  openPanel: (content: React.ReactNode, options?: PushPanelOptions) => void;
  closePanel: () => void;
  subscribe: (listener: (isOpen: boolean) => void) => () => void;
};

type PushPanelOptions = {
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  key?: string;
  title?: string;
};

const panelRef: { current: PanelAPI | null } = { current: null };
let activePanelOwner: symbol | null = null;

export function setPanelRef(api: PanelAPI | null) {
  panelRef.current = api;
}

export function getActivePanelOwner() {
  return activePanelOwner;
}

export function closeRightPanel() {
  activePanelOwner = null;
  panelRef.current?.closePanel();
}

function useIsDesktop() {
  const [d, setD] = React.useState(typeof window !== "undefined" ? window.innerWidth >= 768 : false);
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    setD(mq.matches);
    const h = (e: MediaQueryListEvent) => setD(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return d;
}

const PushPanelContext = React.createContext(false);

function PushPanelPortal({
  children,
  className,
  onClose,
  container,
}: {
  children: React.ReactNode;
  className?: string;
  onClose: () => void;
  container: HTMLElement;
}) {
  // Strip width-constraining classes from the consumer className so the panel
  // always fills 100% of the docked column width (which the user controls via
  // the resize handle). Without this, classes like `max-w-2xl` or `sm:max-w-lg`
  // on SheetContent would leave whitespace when the user widens the column.
  const sanitized = (className ?? "")
    .split(/\s+/)
    .filter((c) => !/^!?(sm:|md:|lg:|xl:|2xl:)?!?(max-w-|w-|min-w-)/.test(c))
    .join(" ");
  return createPortal(
    <PushPanelContext.Provider value={true}>
      <div className="flex flex-col !h-full w-full relative">
        <button
          onClick={() => {
            if (activePanelOwner) activePanelOwner = null;
            panelRef.current?.closePanel();
            onClose();
          }}
          aria-label="Fermer"
          className="absolute right-3 top-3 z-[200] inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground opacity-80 hover:opacity-100 hover:bg-accent hover:text-accent-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">Fermer</span>
        </button>
        {/* `isolate` : les z-index internes (ex. z-[70] du composer d'email)
            restent confinés et ne recouvrent plus le bouton de fermeture. */}
        <div className={cn("isolate flex-1 min-h-0 w-full !max-w-none overflow-y-auto p-4 pt-11", sanitized)}>
          {children}
        </div>
      </div>
    </PushPanelContext.Provider>,
    container,
  );
}


interface SheetRootProps extends SheetPrimitive.DialogProps {
  panelWidth?: PushPanelOptions;
}

function Sheet({ open, onOpenChange, children, panelWidth, ...props }: SheetRootProps) {
  const isDesktop = useIsDesktop();
  const onOpenChangeRef = React.useRef(onOpenChange);
  const prevOpenRef = React.useRef(false);
  const ownerRef = React.useRef<symbol>(Symbol());
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  const [, forceUpdate] = React.useState(0);
  onOpenChangeRef.current = onOpenChange;

  // Inspect direct child Sheet.Content to detect side & forward children.
  let hasRightContent = false;
  let rightChildren: React.ReactNode = null;
  let rightClassName: string | undefined;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      const side = (child as any).props?.side;
      if (!side || side === "right") {
        hasRightContent = true;
        rightChildren = (child as any).props?.children;
        rightClassName = (child as any).props?.className;
      }
    }
  });

  // Push mode is enabled by default for any right-side Sheet whenever a
  // RightPanel container is mounted (i.e. inside AppLayout). On public
  // routes without the container, falls back to the legacy Radix overlay.
  // Consumers can override with explicit panelWidth={...}.
  const hasPanelContainer =
    typeof document !== "undefined" && !!document.getElementById("right-panel-portal-container");
  const effectivePanelWidth: PushPanelOptions =
    panelWidth ?? { defaultWidth: 38, minWidth: 22, maxWidth: 90, key: "default-right-panel" };
  const wantsPushMode = isDesktop && !!panelRef.current && hasPanelContainer && hasRightContent;

  const lockedModeRef = React.useRef<"push" | "overlay" | null>(null);
  const wasPushModeRef = React.useRef(false);
  if (!open) {
    if (lockedModeRef.current !== null) lockedModeRef.current = null;
  } else if (lockedModeRef.current === null) {
    const blocked = activePanelOwner !== null && activePanelOwner !== ownerRef.current;
    lockedModeRef.current = wantsPushMode && !blocked ? "push" : "overlay";
  } else if (lockedModeRef.current === "overlay" && wantsPushMode && activePanelOwner === null) {
    // Push mode became available after this sheet opened (container mounted
    // late, or the previous owner released the panel): upgrade instead of
    // staying stuck in a mode that may never render.
    lockedModeRef.current = "push";
  }
  const usePushMode = wantsPushMode && lockedModeRef.current === "push";
  if (usePushMode) wasPushModeRef.current = true;


  React.useEffect(() => {
    if (!usePushMode) return;
    const panel = panelRef.current;
    if (!panel) return;
    const owner = ownerRef.current;
    return panel.subscribe((panelIsOpen) => {
      if (!panelIsOpen && activePanelOwner === owner) {
        activePanelOwner = null;
        onOpenChangeRef.current?.(false);
      }
      forceUpdate((v) => v + 1);
    });
  }, [usePushMode]);

  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    const wasOpen = prevOpenRef.current;
    const isNowOpen = !!open;
    prevOpenRef.current = isNowOpen;

    if (usePushMode && isNowOpen && panel && (!wasOpen || activePanelOwner !== ownerRef.current)) {
      activePanelOwner = ownerRef.current;
      panel.openPanel(null, {
        defaultWidth: effectivePanelWidth.defaultWidth ?? 35,
        minWidth: effectivePanelWidth.minWidth ?? 20,
        maxWidth: effectivePanelWidth.maxWidth ?? 90,
        key: effectivePanelWidth.key,
        title: effectivePanelWidth.title,
      });
      return;
    }

    if (!isNowOpen && wasOpen && wasPushModeRef.current && panel) {
      if (activePanelOwner === ownerRef.current) activePanelOwner = null;
      panel.closePanel();
      wasPushModeRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, usePushMode]);

  // Unmount cleanup: release ownership if still owned.
  React.useEffect(() => {
    const owner = ownerRef.current;
    return () => {
      if (activePanelOwner === owner) {
        activePanelOwner = null;
        panelRef.current?.closePanel();
      }
    };
  }, []);

  // Resolve the portal target from the DOM once push panel opens.
  React.useEffect(() => {
    if (usePushMode && open) {
      const timer = setTimeout(() => {
        setPortalContainer(document.getElementById("right-panel-portal-container"));
      }, 50);
      return () => clearTimeout(timer);
    }
    setPortalContainer(null);
  }, [usePushMode, open]);

  if (usePushMode) {
    if (open && portalContainer && activePanelOwner === ownerRef.current) {
      return (
        <PushPanelPortal
          container={portalContainer}
          className={rightClassName}
          onClose={() => onOpenChangeRef.current?.(false)}
        >
          {rightChildren}
        </PushPanelPortal>
      );
    }
    if (!open && activePanelOwner === ownerRef.current) {
      activePanelOwner = null;
      queueMicrotask(() => panelRef.current?.closePanel());
    }
    return null;
  }

  return (
    <SheetPrimitive.Root open={open} onOpenChange={onOpenChange} {...props}>
      {children}
    </SheetPrimitive.Root>
  );
}

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 gap-3 bg-background p-5 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top pt-[calc(3.5rem+env(safe-area-inset-top))]",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom pt-14 pb-[calc(1.25rem+env(safe-area-inset-bottom))]",
        left: "inset-y-0 left-0 h-full w-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:w-3/4 sm:max-w-sm overflow-y-auto pt-[calc(3.5rem+env(safe-area-inset-top))] pb-[calc(1.25rem+env(safe-area-inset-bottom))]",
        right:
          "inset-y-0 right-0 h-full w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:w-3/4 sm:max-w-lg overflow-y-auto pt-[calc(3.5rem+env(safe-area-inset-top))] pb-[calc(1.25rem+env(safe-area-inset-bottom))]",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  closeLabel?: string;
  /**
   * Classes appliquées au voile (utile pour relever le niveau de superposition
   * au-dessus d'une couche plein écran, ex. la lecture d'email sur mobile).
   */
  overlayClassName?: string;
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ side = "right", className, children, closeLabel = "Close", overlayClassName, ...props }, ref) => {
    const inPushPanel = React.useContext(PushPanelContext);
    // When mounted inside the docked push panel, render children inline —
    // the panel column already provides positioning, sizing and close button.
    if (inPushPanel) {
      // Strip width-constraining classes from consumer className so the panel
      // always fills 100% of the docked column width (which the user controls
      // via the resize handle).
      const sanitized = (className ?? "")
        .split(/\s+/)
        .filter((c) => !/^!?(sm:|md:|lg:|xl:|2xl:)?!?(max-w-|w-|min-w-)/.test(c))
        .join(" ");
      return (
        <div className={cn("flex flex-col h-full w-full !max-w-none overflow-y-auto", sanitized)}>

          {children}
        </div>
      );
    }
    const closeTop = side === "bottom" ? "top-3" : "top-[calc(0.75rem+env(safe-area-inset-top))]";
    return (
      <SheetPortal>
        <SheetOverlay className={overlayClassName} />
        <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
          {children}
          <SheetPrimitive.Close
            aria-label={closeLabel}
            className={cn(
              "absolute right-3 inline-flex items-center justify-center h-9 w-9 tap-target rounded-md text-muted-foreground opacity-80 ring-offset-background transition-all hover:opacity-100 hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-secondary focus:outline-hidden focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none",
              closeTop,
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{closeLabel}</span>
          </SheetPrimitive.Close>
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  },
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1 pb-3 mb-3 border-b border-border/60 text-left",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 pt-3 mt-3 border-t border-border/60 sm:flex-row sm:justify-end sm:gap-2 sm:space-x-0",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => {
  const inPushPanel = React.useContext(PushPanelContext);
  if (inPushPanel) {
    return <h2 className={cn("text-base font-semibold leading-tight tracking-tight text-foreground", className)} {...(props as any)} />;
  }
  return (
    <SheetPrimitive.Title ref={ref} className={cn("text-base font-semibold leading-tight tracking-tight text-foreground", className)} {...props} />
  );
});
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => {
  const inPushPanel = React.useContext(PushPanelContext);
  if (inPushPanel) {
    return <p className={cn("text-xs text-muted-foreground leading-relaxed", className)} {...(props as any)} />;
  }
  return (
    <SheetPrimitive.Description
      ref={ref}
      className={cn("text-xs text-muted-foreground leading-relaxed", className)}
      {...props}
    />
  );
});
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
